import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Phone, Mail, Loader2, Send, Copy, CalendarPlus, Gift, MessageSquareText } from "lucide-react";
import { smsHref, firstName } from "@/lib/sms";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
const gbpDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
};
const gbpDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso).slice(0, 16);
  }
};

/** How the booking was placed — from the booking's source field. */
export const sourceLabel = (b) => {
  const s = b?.source || "";
  if (s.startsWith("external:pitchup")) return "Pitchup";
  if (s.startsWith("external:booking")) return "Booking.com";
  if (s === "admin-manual") return "Manual (by you)";
  if (s.includes("concierge") || s.includes("daisy")) return "Daisy concierge";
  return "Website";
};

/** How the booking was (or will be) paid. */
export const paymentLabel = (b) => {
  const s = b?.source || "";
  if (s.startsWith("external:pitchup")) return "Paid via Pitchup";
  if (s.startsWith("external:booking")) return "Paid via Booking.com";
  if (b?.payment_status === "paid") {
    if (b?.manually_marked_paid_at && !b?.square_order_id) return "Marked paid manually";
    if (b?.payment_type === "deposit") return `25% deposit paid by card (${gbp(b?.deposit)})`;
    return "Paid in full by card";
  }
  if (b?.payment_status === "pending") return "Awaiting payment";
  if (b?.payment_status === "expired") return "Checkout expired — unpaid";
  if (b?.payment_status === "cancelled") return "Cancelled";
  return b?.payment_status || "—";
};

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${
    status === "paid" ? "bg-[#3B8C4E] text-white" :
    status === "pending" ? "bg-[#C89B5F] text-white" :
    "bg-[#DDD1BE] text-[#14100F]"
  }`}>
    {status}
  </span>
);

/** "Book them in" — minimal-effort manual booking + payment link, prefilled with this customer. */
const QuickBook = ({ detail, email, units, onBooked }) => {
  const [unitId, setUnitId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [guests, setGuests] = useState(2);
  const [dogs, setDogs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const flatUnits = useMemo(() => {
    const out = [];
    (units?.types || []).forEach((t) =>
      (t.units || []).forEach((u) => out.push({ id: u.id, name: u.name, type: t.type }))
    );
    return out;
  }, [units]);

  const submit = async () => {
    if (!unitId || !start || !end) { toast.error("Pick a unit and both dates."); return; }
    const unit = flatUnits.find((u) => u.id === unitId);
    if (!unit) { toast.error("Pick a unit."); return; }
    setBusy(true);
    setResult(null);
    let booking;
    try {
      const { data } = await axios.post(`${API}/admin/bookings`, {
        unit_type: unit.type,
        unit_id: unitId,
        start_date: start,
        end_date: end,
        guests: Number(guests) || 2,
        dogs: Number(dogs) || 0,
        guest_name: detail?.name || "",
        guest_email: (detail?.email || email || "").trim(),
        guest_phone: detail?.phone || "",
        payment_status: "pending",
        source: "admin-manual",
      });
      booking = data.booking;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking.");
      setBusy(false);
      return;
    }
    // Show the result card instantly — the payment link can take a few seconds
    // (Square + email), so it fills in asynchronously below.
    setResult({ booking, checkout_url: "", emailed: false, generating: true });
    setBusy(false);
    toast.success(`Booked in — ${gbp(booking.total)}. Generating payment link…`);
    onBooked?.();
    try {
      const res = await axios.post(`${API}/admin/bookings/${booking.id}/payment-link`);
      const linkData = res.data;
      setResult({ booking, checkout_url: linkData?.checkout_url || "", emailed: !!linkData?.emailed, generating: false });
      toast.success(linkData?.emailed ? "Payment link emailed to the guest." : "Payment link ready — copy it below.");
    } catch {
      setResult({ booking, checkout_url: "", emailed: false, generating: false, linkFailed: true });
      toast.error("Payment link failed — send it from the Bookings tab.");
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied."); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-xl p-4" data-testid="peek-quickbook">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold flex items-center gap-1.5">
        <CalendarPlus size={13} /> Book them in — payment link sent manually
      </p>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="col-span-2 md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">Unit</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="mt-1 h-9" data-testid="peek-qb-unit"><SelectValue placeholder="Pick unit" /></SelectTrigger>
            <SelectContent>
              {flatUnits.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">Arrival</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-9" data-testid="peek-qb-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">Departure</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9" data-testid="peek-qb-end" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">Guests</Label>
          <Input type="number" min={1} max={4} value={guests} onChange={(e) => setGuests(e.target.value)} className="mt-1 h-9" data-testid="peek-qb-guests" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">Dogs</Label>
          <Select value={String(dogs)} onValueChange={(v) => setDogs(Number(v))}>
            <SelectTrigger className="mt-1 h-9" data-testid="peek-qb-dogs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No dogs</SelectItem>
              <SelectItem value="1">1 dog</SelectItem>
              <SelectItem value="2">2 dogs</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-[11px] uppercase tracking-[0.12em] disabled:opacity-60"
            data-testid="peek-qb-submit"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Book &amp; send link
          </button>
        </div>
      </div>
      {result && (
        <div className="mt-3 rounded-lg bg-[#F5EFE6] border border-[#DDD1BE] p-3 text-sm" data-testid="peek-qb-result">
          <p className="font-medium text-[#14100F]">
            {result.booking.booking_id} · {gbp(result.booking.total)} ·{" "}
            {result.generating ? "generating payment link…" : result.linkFailed ? "link failed — use the Bookings tab" : result.emailed ? "payment link emailed to guest" : "link ready — copy below"}
            {result.generating && <Loader2 size={12} className="inline ml-1.5 animate-spin" />}
          </p>
          {result.checkout_url && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => copy(result.checkout_url)}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-white"
                data-testid="peek-qb-copy"
              >
                <Copy size={11} /> Copy payment link
              </button>
              {detail?.phone && (
                <a
                  href={smsHref(detail.phone, `Hi ${firstName(detail.name)}, here's your Walnut Lakes payment link for ${gbp(result.booking.total)}: ${result.checkout_url}`)}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-[#3B8C4E] text-white hover:bg-[#0F0E0E]"
                  data-testid="peek-qb-text"
                >
                  <MessageSquareText size={11} /> Text it
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Shared customer story drawer — used by Rewards, Analytics and anywhere an
 * admin clicks a guest. Shows contact quick-actions, loyalty snapshot,
 * quick-book with payment link, and every booking: when it was booked,
 * how it was booked, how it was paid and the stay dates.
 */
const CustomerPeek = ({ email, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers/${encodeURIComponent(email)}`);
      setDetail(data);
    } catch {
      // No bookings yet — still offer quick-book with just the email
      setDetail({ email, name: "", phone: "", bookings: [], total_bookings: 0, total_spent: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);
  useEffect(() => {
    axios.get(`${API}/units`).then(({ data }) => setUnits(data)).catch(() => {});
  }, []);

  const unitName = useMemo(() => {
    const map = {};
    (units?.types || []).forEach((t) => (t.units || []).forEach((u) => { map[u.id] = u.name; }));
    return (id) => map[id] || id;
  }, [units]);

  const sendLink = async (bookingId) => {
    setSendingId(bookingId);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${bookingId}/payment-link`);
      if (data.emailed) toast.success("Payment link emailed to the guest.");
      else toast.success("Link ready — email failed, copy it instead.");
      try { await navigator.clipboard.writeText(data.checkout_url); } catch {}
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not generate link.");
    } finally {
      setSendingId(null);
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied."); }
    catch { toast.error("Copy failed."); }
  };

  if (!email) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-[#F5EFE6] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="customer-peek">
        <div className="sticky top-0 bg-[#F5EFE6] border-b border-[#DDD1BE] p-5 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Customer story</p>
            <h4 className="font-serif text-2xl" data-testid="peek-name">{detail?.name || email}</h4>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#DDD1BE]" data-testid="peek-close"><X size={18} /></button>
        </div>

        {loading && <div className="p-8 text-center text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}

        {!loading && detail && (
          <div className="p-5 md:p-6 space-y-5">
            {/* Contact + quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Phone</p>
                {detail.phone ? (
                  <a href={`tel:${detail.phone.replace(/\s/g, "")}`} className="mt-1 flex items-center gap-1 font-mono text-sm text-[#14100F] hover:text-[#B76E79]" data-testid="peek-call">
                    <Phone size={13} /> {detail.phone}
                  </a>
                ) : <p className="mt-1 text-[#6A5F58]">—</p>}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Email</p>
                <a href={`mailto:${detail.email}`} className="mt-1 flex items-center gap-1 text-xs text-[#14100F] hover:text-[#B76E79] break-all" data-testid="peek-email">
                  <Mail size={13} /> {detail.email}
                </a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Loyalty</p>
                <p className="mt-1 text-xs flex items-center gap-1" data-testid="peek-loyalty">
                  <Gift size={12} className="text-[#B76E79]" />
                  {detail.ledger ? (
                    <>Camp {detail.ledger.touring_nights || 0}/7 · Lodge {detail.ledger.lodge_nights || 0}/10</>
                  ) : (
                    <span className="text-[#6A5F58]">no ledger yet</span>
                  )}
                </p>
              </div>
            </div>

            <QuickBook detail={detail} email={email} units={units} onBooked={load} />

            {/* Booking history */}
            <div>
              <p className="overline text-[#B76E79] mb-3">Bookings ({detail.total_bookings || 0}) · lifetime spend {gbp(detail.total_spent)}</p>
              <div className="space-y-2">
                {(detail.bookings || []).length === 0 && (
                  <p className="text-sm text-[#6A5F58]">No bookings on record for this customer yet.</p>
                )}
                {(detail.bookings || []).map((b) => (
                  <div key={b.id} className="bg-white border border-[#DDD1BE] rounded-lg p-3 text-sm" data-testid={`peek-booking-${b.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-medium">{unitName(b.unit_id)} · {gbpDate(b.start_date)} → {gbpDate(b.end_date)}</p>
                        <p className="text-[11px] text-[#6A5F58] mt-0.5">
                          {b.nights} night{b.nights > 1 ? "s" : ""} · {b.guests} guest{b.guests > 1 ? "s" : ""} · {gbp(b.total)}
                        </p>
                      </div>
                      <StatusPill status={b.payment_status} />
                    </div>
                    {/* The story: when booked, how booked, how paid */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]" data-testid={`peek-when-${b.id}`}>
                        Booked {gbpDateTime(b.created_at)}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]" data-testid={`peek-how-${b.id}`}>
                        via {sourceLabel(b)}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]" data-testid={`peek-paid-${b.id}`}>
                        {paymentLabel(b)}
                      </span>
                    </div>
                    {b.payment_status === "pending" && !(b.source || "").startsWith("external:") && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => sendLink(b.id)}
                          disabled={sendingId === b.id}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white disabled:opacity-50"
                          data-testid={`peek-send-${b.id}`}
                        >
                          {sendingId === b.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                          Send payment link
                        </button>
                        {b.checkout_url && (
                          <button
                            onClick={() => copyLink(b.checkout_url)}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-white"
                            data-testid={`peek-copy-${b.id}`}
                          >
                            <Copy size={10} /> Copy link
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPeek;
